/*
 * AuthContext — cookie-based session (httpOnly).
 *
 * Post-migration behaviour (Feb 2026):
 *   • JWT lives ONLY in the httpOnly `mm_session` cookie set by the backend.
 *     JS cannot read it → XSS cannot exfiltrate the session token.
 *   • CSRF protection: backend sets a paired `mm_csrf` cookie (non-httpOnly)
 *     that JS reads and echoes back in the `X-CSRF-Token` header on every
 *     state-changing request (POST/PUT/PATCH/DELETE). Configured via a global
 *     axios interceptor at module load.
 *   • On mount, we call GET /api/auth/me — if the cookie is valid, we get
 *     back the user object. If not, we stay signed-out. No localStorage.
 *   • `authHeaders` is now an empty object (kept as a no-op shim so we don't
 *     have to touch every call site). All auth flows through cookies.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CSRF_COOKIE = "mm_csrf";

// -------- Global axios wiring (idempotent) --------
axios.defaults.withCredentials = true;
if (!axios.__mmCsrfInstalled) {
  axios.interceptors.request.use((config) => {
    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const raw = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${CSRF_COOKIE}=`));
      if (raw) {
        // Use substring (not split) — safe against base64 '=' padding chars.
        const value = decodeURIComponent(raw.substring(raw.indexOf("=") + 1));
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = value;
      }
    }
    return config;
  });
  axios.__mmCsrfInstalled = true;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'login' | 'register'

  // On mount: probe /me — if a valid httpOnly cookie exists we get the user.
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/auth/me`)
      .then((r) => { if (!cancelled) setUser(r.data.user); })
      .catch(() => { /* not signed in — leave user=null */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const pinLogin = useCallback(async (email, pin) => {
    const { data } = await axios.post(`${API}/auth/pin/login`, { email, pin });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    setUser(data.user);
    return data.user;
  }, []);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.warn("[auth] logout request failed", err?.message);
    }
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await axios.put(`${API}/auth/me`, payload);
    setUser(data.user);
    return data.user;
  }, []);

  // Compat shim — kept as an empty object so components passing
  // `headers={authHeaders}` don't need to be rewritten. Cookies handle auth.
  const authHeaders = useMemo(() => ({}), []);

  const value = useMemo(
    () => ({
      user, loading, login, pinLogin, register, googleSession, logout, updateProfile,
      modal, setModal, authHeaders,
      isAuthenticated: !!user,
      isAdmin: !!user?.is_admin,
      isStaff: !!user?.is_staff,
      canStaff: !!(user?.is_admin || user?.is_staff),
      // token is intentionally not exposed — kept only in the httpOnly cookie.
      // Legacy callers that read `token` will now see `undefined`; adjust as needed.
    }),
    [user, loading, modal, authHeaders, login, pinLogin, register, googleSession, logout, updateProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
